import { useMemo } from 'react';
import { useUser } from '../../../../../global/auth/useUser';
import { useOrgInfo } from '../../../../../global/auth/use-org-info/use-org-info';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_MY_ACHIEVEMENTS } from './my-achievements.gql';

export interface MyAchievementLevel {
  achievementLevelId: string;
  name: string;
  position: number;
  threshold?: number | null;
  badgeDesign: string | null;
}

export interface MyAward {
  awardedAchievementId: string;
  awardedAt: string;
  seenAt: string | null;
  level: MyAchievementLevel & { achievement: { achievementId: string; name: string } };
}

export interface MyCertificate {
  volunteerCertificateId: string;
  templateName: string;
  awardedFor: string | null;
  issuedAt: string;
  seenAt: string | null;
}

interface GetMyAchievements {
  vm: {
    volunteer: {
      volunteerId: string;
      awardedAchievements: MyAward[] | null;
      certificates: MyCertificate[] | null;
      achievementProgress: { achievementId: string; value: number }[] | null;
    } | null;
    achievements: {
      achievementId: string;
      name: string;
      metric: string;
      isEnabled: boolean;
      showProgress: boolean;
      levels: MyAchievementLevel[];
    }[];
  };
}

export const useMyAchievements = () => {
  const { getVolunteerIdentity } = useUser();
  const { volunteerId } = getVolunteerIdentity();
  const org = useOrgInfo();
  const enabled = org?.packageConfig?.achievements ?? true;

  const { data, loading, refetch } = useCampfireQuery<GetMyAchievements, { volunteerId: string }>(GET_MY_ACHIEVEMENTS, {
    options: { variables: { volunteerId }, skip: !enabled },
  });

  return useMemo(() => {
    const awards = [...(data?.vm.volunteer?.awardedAchievements ?? [])].sort((a, b) =>
      b.awardedAt.localeCompare(a.awardedAt)
    );
    const certificates = [...(data?.vm.volunteer?.certificates ?? [])].sort((a, b) =>
      b.issuedAt.localeCompare(a.issuedAt)
    );
    const newCount = awards.filter((a) => !a.seenAt).length + certificates.filter((c) => !c.seenAt).length;
    return {
      enabled,
      loading,
      loaded: Boolean(data),
      refetch,
      awards,
      certificates,
      achievements: data?.vm.achievements ?? [],
      progress: new Map((data?.vm.volunteer?.achievementProgress ?? []).map((p) => [p.achievementId, p.value])),
      hasAny: awards.length > 0 || certificates.length > 0,
      newCount,
    };
  }, [data, loading, enabled]);
};
